import { useState, useCallback, useEffect, useRef } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Alert from "@mui/material/Alert";
import { useSnackbar } from "notistack";

import {
  useLazyLookupAccountQuery,
  type AccountLookupResult,
} from "../services/api";

interface AccountLookupProps {
  /** Called when the looked-up account changes (found, cleared, or errored). */
  onAccountChange?: (account: AccountLookupResult | null) => void;
  /** Pre-fill the email field (e.g. from query params). */
  initialEmail?: string;
}

function AccountLookup({ onAccountChange, initialEmail }: AccountLookupProps) {
  const [email, setEmail] = useState(initialEmail ?? "");
  const { enqueueSnackbar } = useSnackbar();

  const [lookupAccount, { data: account, isFetching, error }] =
    useLazyLookupAccountQuery();

  // Sync initialEmail prop into state (for query-param auto-population).
  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  // Keep a stable ref to the callback so the handler never goes stale.
  const onChangeRef = useRef(onAccountChange);
  onChangeRef.current = onAccountChange;

  const handleLookup = useCallback(async () => {
    try {
      const result = await lookupAccount(email).unwrap();
      onChangeRef.current?.(result);
    } catch {
      enqueueSnackbar("Account not found.", { variant: "error" });
      onChangeRef.current?.(null);
    }
  }, [email, lookupAccount, enqueueSnackbar]);

  const is404 =
    error && "status" in error && (error as { status: number }).status === 404;

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          label="Email"
          type="email"
          placeholder="Enter account email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && email && !isFetching) handleLookup();
          }}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleLookup}
          disabled={isFetching || !email}
          sx={{ whiteSpace: "nowrap" }}
        >
          {isFetching ? "Looking up..." : "Lookup"}
        </Button>
      </Stack>
      <Divider />

      {is404 && <Alert severity="warning">No account found for {email}.</Alert>}

      {account && !error && (
        <Paper sx={{ p: 2 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: "bold" }}>
                  Email
                </TableCell>
                <TableCell>{account.email}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: "bold" }}>
                  Expires Date
                </TableCell>
                <TableCell>{account.expires_date ?? "—"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: "bold" }}>
                  Expires
                </TableCell>
                <TableCell>{account.expires ?? "—"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" sx={{ fontWeight: "bold" }}>
                  Comment
                </TableCell>
                <TableCell>{account.comment ?? "—"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}
    </>
  );
}

export { AccountLookup };
export type { AccountLookupProps };
